import '../../platform.css';
import './searchPlayers.css';

import { useEffect, useRef, useState, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { playersState } from '../../recoil/atoms/playersState';
import { playerOverviewsState } from '../../recoil/atoms/playerOverviewsState';
import { useSetSelectedPlayer } from '../../recoil/hooks/useSetSelectedPlayer';
import { scoutingConfigState } from '../../recoil/atoms/scoutingConfigState';
import { useAuthContext } from '../../../common/contexts/useAuthContext';

import Fuse from 'fuse.js';

import SwipeRightIcon from '@mui/icons-material/SwipeRight';

import { translate } from '../../../common/language/translations';
import { PlayerSimpleTable } from '../tables/playerSimpleTable/PlayerSimpleTable';
import { InputField } from '../controls/input/InputField';
import { debounce } from '../../utils/commonUtils';
import { searchPlayerOverviews } from '../../services/server/application/playerOverviews';
import { playerSimpleTablePageSize } from '../../views/scouting/Scouting';
import { getSortDistanceRole } from '../../utils/playerUtils';
import { getPlayerSimpleTableRowHeight } from '../tables/playerSimpleTable/playerSimpleColumns';
import { PlayerDocument, PlayerId, PlayerOverview } from '../../types/player';
import { StringToAnyMap } from '../../types/common';
import { PlayerOverviewsQueryOptions, SearchPlayerOverviewsResponse } from '../../types/scouting';


interface AddPlayerViewProps {
  searchString: string;
  setSearchString: (value: string) => void;

  isOwnTeam: boolean;
  isAcademyTeam: boolean;
  isTabLine: boolean;

  suggestedOwnTeamPlayers: PlayerOverview[];

  isAddPlayerWithoutData: boolean;
  setIsAddPlayerWithoutData: (value: boolean) => void;

  draggedPlayer: StringToAnyMap | undefined;
  setDraggedPlayer: (player: StringToAnyMap | undefined) => void;
  playerWasJustDropped: boolean;
}

export const AddPlayerView: React.FC<AddPlayerViewProps> = ({
  searchString,
  setSearchString,

  isOwnTeam,
  isAcademyTeam,
  isTabLine,

  suggestedOwnTeamPlayers,

  isAddPlayerWithoutData,
  setIsAddPlayerWithoutData,

  draggedPlayer,
  setDraggedPlayer,
  playerWasJustDropped,
}) => {

  const { currentUser } = useAuthContext();
  const userConfig = useRecoilValue(userConfigState);
  const scoutingConfig = useRecoilValue(scoutingConfigState);

  const players = useRecoilValue(playersState);
  const playerOverviews = useRecoilValue(playerOverviewsState);
  const { setAndTrackSelectedPlayer } = useSetSelectedPlayer();

  const [academyPlayers, setAcademyPlayers] = useState<StringToAnyMap[]>([]);
  const [academyPlayersToShow, setAcademyPlayersToShow] = useState<StringToAnyMap[]>([]);
  const [academyPlayersSearchString, setAcademyPlayersSearchString] = useState<string>('');

  const [existingPlayersWithoutData, setExistingPlayersWithoutData] = useState<PlayerDocument[]>([]);
  const [existingPlayersWithoutDataToShow, setExistingPlayersWithoutDataToShow] = useState<PlayerDocument[]>([]);
  const [existingPlayersWithoutDataSearchString, setExistingPlayersWithoutDataSearchString] = useState<string>('');

  const [debouncedSearchString, setDebouncedSearchString] = useState<string>('');
  const clearDebounceRef = useRef<() => void>(() => null);

  const [activeSearchString, setActiveSearchString] = useState('');
  const [shouldShowSearchResult, setShouldShowSearchResult] = useState(false);
  const [isSearchWithoutResult, setIsSearchWithoutResult] = useState(false);

  const [tableData, setTableData] = useState<StringToAnyMap[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalHits, setTotalHits] = useState(0);

  const [showText, setShowText] = useState(false);


  const onPlayerClick = (player: StringToAnyMap) => {
    const playerId = Number(player.id);
    const isPlayerWithData = !isNaN(playerId);

    const playerOverview = playerOverviews[playerId] ?? (
      isPlayerWithData
        ? player
        : undefined
    );

    setAndTrackSelectedPlayer({
      id: player.id,
      name: player.name,
      playerOverview: playerOverview,
    });
  };


  const onChangeSearchField = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(event.target.value);
  };


  const onKeyDownSearchField = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (!isAddPlayerWithoutData) {
        handleSearchForPlayers(true, true);
      }
    }
  };


  const setEmptyTableData = () => {
    setTableData([]);
    setCurrentPage(0);
    setTotalHits(0);
  };


  const handleSearchForPlayers = async (isUserInitiated: boolean, isNewSearch: boolean) => {

    setIsLoading(true);

    if (isUserInitiated) {
      clearDebounceRef.current();
    }

    if (searchString !== '') {

      const nextPageToQuery = isNewSearch ? 1 : currentPage + 1;
      const queryOptions: PlayerOverviewsQueryOptions = {
        name: searchString,
        page: nextPageToQuery,
        pageSize: playerSimpleTablePageSize,
      };

      const result: SearchPlayerOverviewsResponse | undefined = await searchPlayerOverviews(
        queryOptions,
        currentUser,
        scoutingConfig.nameSearchHistory,
        userConfig?.email,
        userConfig?.club,
      );
      setActiveSearchString(searchString);

      if (!result) {
        // handle error?
        setEmptyTableData();
      }
      else if (result.total_hits === 0 || (isNewSearch && result.players.length === 0)) {
        setIsSearchWithoutResult(true);
        setEmptyTableData();
      }
      else {
        setIsSearchWithoutResult(false);
        setTableData(isNewSearch ? result.players : [...tableData, ...result.players]);
        setCurrentPage(result.current_page);
        setTotalHits(result.total_hits);
      }
    }

    setIsLoading(false);
  };


  const handleMessageLinkClick = () => {
    setSearchString('');
    if (isAddPlayerWithoutData) {
      setIsAddPlayerWithoutData(false);
    }
    else {
      setIsAddPlayerWithoutData(true);
    }
  };


  useEffect(() => {
    if (players) {
      const existingPlayersWithoutData: StringToAnyMap[] = [];

      Object.keys(players).forEach((playerId: PlayerId) => {
        if (isNaN(Number(playerId))) {
          existingPlayersWithoutData.push({ ...players[playerId] });
        }
      });

      setExistingPlayersWithoutData(existingPlayersWithoutData.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')));
    }
  }, [players]);


  useEffect(() => {
    if (existingPlayersWithoutDataSearchString === '') {
      setExistingPlayersWithoutDataToShow(existingPlayersWithoutData);
    }
    else {
      const fuse = new Fuse(existingPlayersWithoutData, { keys: ['name'], threshold: 0.2 });
      setExistingPlayersWithoutDataToShow(fuse.search(existingPlayersWithoutDataSearchString).map(result => result.item));
    }
  }, [existingPlayersWithoutData, existingPlayersWithoutDataSearchString]);


  useEffect(() => {
    const academy: StringToAnyMap[] = [];

    if ((isOwnTeam || isAcademyTeam) && players && playerOverviews && userConfig?.accessConfig.hasAccessToAcademy) {
      Object.keys(players).forEach(playerId => {
        const playerDocument = players[playerId];
        if (playerDocument.isAcademyPlayer) {
          const player: StringToAnyMap = { ...playerDocument };

          if (playerOverviews[player.id]) {
            player['image_url'] = playerOverviews[Number(player.id)]['image_url'];
            player['name'] = playerOverviews[Number(player.id)]['name'];
            player['position_info'] = playerOverviews[Number(player.id)]['position_info'];
          }

          academy.push(player);
        }
      });
    }

    academy.sort((a, b) => getSortDistanceRole(a, b, 'academyRole'));
    setAcademyPlayers(academy);

  }, [players, playerOverviews, isOwnTeam, isAcademyTeam, userConfig?.accessConfig.hasAccessToAcademy]);


  useEffect(() => {
    if (academyPlayersSearchString === '') {
      setAcademyPlayersToShow(academyPlayers);
    }
    else {
      const fuse = new Fuse(academyPlayers, { keys: ['name'], threshold: 0.2 });
      setAcademyPlayersToShow(fuse.search(academyPlayersSearchString).map(result => result.item));
    }
  }, [academyPlayers, academyPlayersSearchString]);


  const resetSearch = () => {
    setDebouncedSearchString('');
    setShouldShowSearchResult(false);
    setEmptyTableData();
    setIsSearchWithoutResult(false);
  };


  useEffect(() => {
    if (searchString.length === 0) {
      resetSearch();
    }

    else if (isAddPlayerWithoutData) {
      setShouldShowSearchResult(true);
      setIsSearchWithoutResult(false);
      setTableData(
        [{
          id: 'newPlayerWithoutData',
          name: searchString,
        }]
      );
    }

    if (!isAddPlayerWithoutData) {
      const [debounceFilter, clearDebounce] = debounce(() => {
        if (searchString !== debouncedSearchString) {
          setDebouncedSearchString(searchString);
        }
      }, 600);

      debounceFilter();

      clearDebounceRef.current = clearDebounce;

      return () => {
        clearDebounce();
      };
    }
  }, [searchString]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (debouncedSearchString !== '') {
      handleSearchForPlayers(false, true);
    }
  }, [debouncedSearchString]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const timer = setTimeout(() => {
      setShowText(true);
    }, 250);

    return () => clearTimeout(timer);
  }, []);


  useEffect(() => {
    if (searchString !== '' && searchString === activeSearchString) {
      setShouldShowSearchResult(true);
    }
  }, [searchString, activeSearchString]);


  // need to calculate max heights of academyTable and suggestedOwnTeamTable based on the number of players in the tables
  const tableHeights = useMemo(() => {
    const totalHeight = isTabLine ? '67vh' : '71vh';

    let fixedPxHeight = 0;
    let fixedVhHeight = 0;

    if (academyPlayers.length > 0) {
      // title offset
      fixedPxHeight += 15;
      fixedVhHeight += 2;
    }

    if (academyPlayers.length > 12) {
      // input field offset
      fixedPxHeight += 28;
      fixedVhHeight += 2;
    }

    if (suggestedOwnTeamPlayers.length > 0) {
      // title offset
      fixedPxHeight += 15;
      fixedVhHeight += 2;
    }

    const bothTableHavePlayers = suggestedOwnTeamPlayers.length > 0 && academyPlayers.length > 0;
    if (bothTableHavePlayers) {
      // gap offset
      fixedVhHeight += 3;
    }

    const availableTableHeight = 'calc(' + totalHeight + ' - ' + fixedVhHeight + 'vh - ' + fixedPxHeight + 'px)';
    if (!bothTableHavePlayers) {
      return {
        suggestedOwnTeam: availableTableHeight,
        academy: availableTableHeight,
        total: totalHeight,
      };
    }

    // calculate minimum heights needed for each table based on number of rows
    // if any table has at least 3 rows, 3 rows will be shown
    const rowHeight = getPlayerSimpleTableRowHeight(undefined, undefined);
    const minRows = 3;
    const suggestedOwnTeamMinHeight = Math.min(suggestedOwnTeamPlayers.length, minRows) * rowHeight;
    const academyMinHeight = Math.min(academyPlayers.length, minRows) * rowHeight;

    // calculate how much space is left after reserving minimum heights
    const totalMinHeight = suggestedOwnTeamMinHeight + academyMinHeight;
    const remainingHeight = `calc(${availableTableHeight} - ${totalMinHeight}px)`;

    // calculate how to distribute the remaining space proportionally
    const totalPlayers = suggestedOwnTeamPlayers.length + academyPlayers.length;
    const suggestedOwnTeamPercentage = (suggestedOwnTeamPlayers.length / totalPlayers) * 100;
    const academyPercentage = (academyPlayers.length / totalPlayers) * 100;

    // each table gets its minimum height plus a proportional share of remaining space
    const suggestedOwnTeamHeight = `min(
      calc(${suggestedOwnTeamMinHeight}px + (${remainingHeight} * ${suggestedOwnTeamPercentage} / 100)),
      ${availableTableHeight}
    )`;

    const academyHeight = `min(
      calc(${academyMinHeight}px + (${remainingHeight} * ${academyPercentage} / 100)),
      ${availableTableHeight}
    )`;

    return {
      suggestedOwnTeam: suggestedOwnTeamHeight,
      academy: academyHeight,
      total: totalHeight,
    };
  }, [isTabLine, academyPlayers.length, suggestedOwnTeamPlayers.length]);


  return (
    <div className='add-player-view-container'>

      {showText && tableData.length > 0 && shouldShowSearchResult && (
        <div className='add-player-view-swipe-icon' style={{ top: isAddPlayerWithoutData ? '21vh' : '19vh' }}>
          <SwipeRightIcon style={{ fontSize: 22 }} />
        </div>
      )}

      <div className='add-player-view-message-link fade-in' onClick={handleMessageLinkClick}>
        {showText && translate(isAddPlayerWithoutData ? 'searchForPlayer?' : 'addPlayerWithoutData?', userConfig?.language)}
      </div>

      {isAddPlayerWithoutData && (
        <div className='add-player-view-title'>
          {showText && translate('addPlayerWithoutData', userConfig?.language)}
        </div>
      )}

      {showText && (
        <div
          style={{
            marginTop: isAddPlayerWithoutData ? '9vh' : '4vh',
            width: !isAddPlayerWithoutData && academyPlayers.length > 0 ? 228 : undefined,
            zIndex: 10,
          }}
          className='add-player-view-input-field-container'
        >
          <InputField
            value={searchString}
            placeholder={showText ? translate(isAddPlayerWithoutData ? 'providePlayerName' : 'searchForName', userConfig?.language) : undefined}
            onChange={onChangeSearchField}
            onKeyDown={onKeyDownSearchField}
            resetValue={() => setSearchString('')}
            showNameSearchHistory={!isAddPlayerWithoutData}
            onNameSearchHistoryClick={(name: string) => { setSearchString(name); setDebouncedSearchString(name); }}
            maxHeight={isTabLine ? '71vh' : '75vh'}
            isWhiteBackgroundWhenExpanded={true}
          />
        </div>
      )}

      {shouldShowSearchResult && (
        <div className='add-player-view-result-section'>

          {isSearchWithoutResult && (
            <div className='add-player-view-no-result fade-in'>
              {translate('noResult', userConfig?.language)}
            </div>
          )}

          {!isSearchWithoutResult && !(isAddPlayerWithoutData && playerWasJustDropped) && (
            <div className='add-player-view-table'>
              <PlayerSimpleTable
                tableData={tableData}
                onPlayerClick={(player: StringToAnyMap) => onPlayerClick(player)}
                tableType={isAddPlayerWithoutData ? 'addPlayerViewTeamIcon' : 'addPlayerView'}
                maxHeight={isTabLine ? '70vh' : '74vh'}
                positionKey={'addPlayerTable'}
                draggedPlayer={draggedPlayer}
                setDraggedPlayer={setDraggedPlayer}

                isSearchResultTable={true}
                isLoading={isLoading}
                handleSearchForPlayers={handleSearchForPlayers}
                currentPage={currentPage}
                totalHits={totalHits}
              />
            </div>
          )}

        </div>
      )}


      {showText
        && (academyPlayers.length > 0 || suggestedOwnTeamPlayers.length > 0)
        && !isAddPlayerWithoutData
        && !shouldShowSearchResult
        && searchString === ''
        && (
          <div
            className='add-player-view-existing-players'
            style={{
              height: tableHeights.total,
              marginTop: '14vh',
              justifyContent: academyPlayers.length > 0 && suggestedOwnTeamPlayers.length > 0 ? 'space-evenly' : 'center',
              gap: '3vh',
            }}
          >
            {suggestedOwnTeamPlayers.length > 0 && (
              <div className='add-player-view-existing-players-section'>
                <div className='add-player-view-existing-players-title'>
                  {translate('suggestions', userConfig?.language)}
                </div>

                <div className='add-player-view-players-without-data-table' style={{ maxHeight: '100%' }}>
                  <PlayerSimpleTable
                    tableData={suggestedOwnTeamPlayers}
                    onPlayerClick={(player: StringToAnyMap) => onPlayerClick(player)}
                    tableType={'addPlayerViewAcademyRoleIcon'}
                    maxHeight={tableHeights.suggestedOwnTeam}
                    positionKey={'addPlayerTableExistingPlayers'}
                    draggedPlayer={draggedPlayer}
                    setDraggedPlayer={setDraggedPlayer}
                  />
                </div>
              </div>
            )}

            {academyPlayers.length > 0 && (
              <div className='add-player-view-existing-players-section'>
                <div className='add-player-view-existing-players-title'>
                  {translate('academyPlayers', userConfig?.language)}
                </div>

                {academyPlayers.length > 12 && (
                  <div className='add-player-view-input-field-container-existing-players'>
                    <InputField
                      value={academyPlayersSearchString}
                      placeholder={showText ? translate('searchForName', userConfig?.language) : undefined}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAcademyPlayersSearchString(event.target.value)}
                      resetValue={() => setAcademyPlayersSearchString('')}
                    />
                  </div>
                )}

                <div className='add-player-view-players-without-data-table' style={{ height: academyPlayers.length > 12 ? tableHeights.academy : undefined }}>
                  <PlayerSimpleTable
                    tableData={academyPlayersToShow}
                    onPlayerClick={(player: StringToAnyMap) => onPlayerClick(player)}
                    tableType={'addPlayerViewAcademyRoleIcon'}
                    maxHeight={tableHeights.academy}
                    positionKey={'addPlayerTableExistingPlayers'}
                    draggedPlayer={draggedPlayer}
                    setDraggedPlayer={setDraggedPlayer}
                  />
                </div>
              </div>
            )}
          </div>
        )
      }


      {isAddPlayerWithoutData && existingPlayersWithoutData.length > 0 && (
        <div
          className='add-player-view-existing-players'
          style={{
            height: isTabLine ? '54vh' : '58vh',
            marginTop: '27vh',
            justifyContent: existingPlayersWithoutData.length > 12 ? undefined : 'center'
          }}
        >
          <div className='add-player-view-existing-players-title'>
            {translate('existingPlayersWithoutData', userConfig?.language)}
          </div>

          {existingPlayersWithoutData.length > 12 && (
            <div className='add-player-view-input-field-container-existing-players'>
              <InputField
                value={existingPlayersWithoutDataSearchString}
                placeholder={showText ? translate('searchForName', userConfig?.language) : undefined}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setExistingPlayersWithoutDataSearchString(event.target.value)}
                resetValue={() => setExistingPlayersWithoutDataSearchString('')}
              />
            </div>
          )}

          <div className='add-player-view-players-without-data-table'>
            <PlayerSimpleTable
              tableData={existingPlayersWithoutDataToShow}
              onPlayerClick={(player: StringToAnyMap) => onPlayerClick(player)}
              tableType={'addPlayerViewTeamIcon'}
              maxHeight={'calc(' + (isTabLine ? '50vh' : '54vh') + ' - ' + (existingPlayersWithoutData.length > 12 ? '50px' : '10px') + ')'}
              positionKey={'addPlayerTableExistingPlayers'}
              draggedPlayer={draggedPlayer}
              setDraggedPlayer={setDraggedPlayer}
            />
          </div>
        </div>
      )}

    </div>
  );
};
